// Cascade animation playback for Letter Flick (Phase 7).
//
// Walks a TrajectoryStep[] produced by applyFlick and animates each moving
// tile with `transform: translate3d(...)` + `transition: transform Xms linear`.
// Steps play sequentially: a step starts once the previous step's
// `transitionend` fires. When the final step's transition ends, the caller's
// onComplete callback fires — the controller uses that hook to run its
// canonical re-render of the board + bar.
//
// Design constraints (per Phase 7 plan §6):
//   - Tiles in motion are looked up by tokenIdx through a Map built BEFORE
//     the canonical re-render so the DOM element refs correspond to the
//     pre-flick positions (the cells the animator visually slides FROM).
//   - The transform on a step's element is left applied through the end of
//     the cascade — the canonical re-render at onComplete replaces the entire
//     board DOM so the lingering transforms are GC'd along with the elements.
//   - prefers-reduced-motion users get NO animation: shouldReduceMotion()
//     returns true → animateCascade short-circuits to a microtask onComplete.
//   - Defensive setTimeout fallback (1.5x expected duration) covers the rare
//     case where `transitionend` doesn't fire (page hidden, browser bug, etc.)
//     — without it, isAnimating would never clear and the player would be
//     permanently input-locked.
//
// Pure-ish: depends only on DOM + the TrajectoryStep type. No GameState refs;
// no persistence calls. Pass everything in via opts so the module is trivial
// to unit-test with a JSDOM fixture.

import type { Direction, TokenIdx, TrajectoryStep } from './state'
import { EXIT_CELL } from './state'

// Matches the iOS LetterFlickViewModel cascade speed (FLICK_CELLS_PER_SECOND).
// Per-step duration = step.distance * MS_PER_CELL. Single source of truth;
// changing this here changes the entire game's flick speed.
export const CELLS_PER_SECOND = 12 as const
export const MS_PER_CELL = 1000 / CELLS_PER_SECOND // ≈ 83.33 ms

export interface AnimateCascadeOptions {
  // The board root element. Reserved for future use (current implementation
  // only reads from the lookup); accepted so callers don't need to refactor
  // the call site later if we add board-scoped operations.
  readonly boardRoot: HTMLElement
  readonly trajectory: readonly TrajectoryStep[]
  // Built by the controller via buildTokenDomLookup() against the PRE-flick
  // rendered board. The animator looks up each step's mover by tokenIdx.
  readonly tokenDomLookup: ReadonlyMap<TokenIdx, HTMLElement>
  // Pixel size of a single board cell — measured from the live DOM by the
  // controller (`boardEl.getBoundingClientRect().width / BOARD_SIZE`).
  readonly cellSizePx: number
  // Fires after the cascade completes (or immediately, in a microtask, when
  // reduced motion is active). Wrapping in a microtask in the reduced-motion
  // path keeps the orchestrator's observable ordering aligned with the
  // animated path (controller sees onComplete on a separate tick either way).
  readonly onComplete: () => void
}

// Schedule playback of `trajectory`. Returns synchronously; the cascade
// unfolds across multiple animation frames + transitionend ticks.
export function animateCascade(opts: AnimateCascadeOptions): void {
  if (shouldReduceMotion() || opts.trajectory.length === 0) {
    // Microtask defer so the caller's persist + maybeFireGameEnd happen after
    // the controller has returned from dispatchAndRender — matches the
    // animated path's ordering, prevents reentrance surprises.
    queueMicrotask(opts.onComplete)
    return
  }

  let i = 0
  const runStep = (): void => {
    if (i >= opts.trajectory.length) {
      opts.onComplete()
      return
    }
    const step = opts.trajectory[i]
    i += 1
    // noUncheckedIndexedAccess narrowing — bounds-checked above, but TS still
    // requires the type guard.
    if (step === undefined) {
      opts.onComplete()
      return
    }
    const el = opts.tokenDomLookup.get(step.tokenIdx)
    if (el === undefined) {
      // Defensive: skip to the next step if a tile's DOM ref is missing. This
      // shouldn't happen in normal play (the lookup is built from the same
      // GameState the trajectory references), but a stale lookup or
      // mid-cascade DOM mutation would land us here.
      runStep()
      return
    }

    if (step.distance === 0) {
      // Pass-through cascade member: this token's strike target is immediately
      // adjacent, so the token stays put and instantly transfers motion to the
      // next struck token. No transform write, no transition, no cell--moving
      // class — just chain to the next step on a microtask to preserve the
      // sequential visual handoff timing for later steps in the cascade.
      queueMicrotask(runStep)
      return
    }

    const [dx, dy] = computeTranslate(step.direction, step.distance, opts.cellSizePx)
    const durationMs = step.distance * MS_PER_CELL
    el.classList.add('cell--moving')
    el.style.transition = `transform ${String(durationMs)}ms linear`

    // Single-fire end handler: either transitionend or the fallback timer can
    // drive it; whichever fires first wins. Symmetric cleanup so the alternate
    // handler doesn't leave a dangling listener / timer.
    //
    // Declare the timer holder BEFORE onEnd so onEnd closes over a binding
    // that's already initialized — no temporal-dead-zone fragility from
    // referencing a const that's assigned later in the same scope.
    let fired = false
    let timerId: number | undefined
    const onEnd = (e?: TransitionEvent): void => {
      // Accept only THIS element's own transform transition. transitionend
      // bubbles, and a stray end event (a child's transition, a different
      // property, or a canceled cell-owned transition surfaced as an end on
      // quirky engines) once advanced the cascade early — the struck tile
      // slid in parallel with the flicker. The fallback timer calls with no
      // event and is always accepted.
      if (e !== undefined && (e.target !== el || e.propertyName !== 'transform')) {
        return
      }
      if (fired) return
      fired = true
      el.removeEventListener('transitionend', onEnd)
      if (timerId !== undefined) {
        window.clearTimeout(timerId)
        timerId = undefined
      }
      // Note: we deliberately do NOT clear .cell--moving or the transform
      // here. The next step animates a DIFFERENT element (the struck token),
      // so its previous transform is irrelevant. The canonical re-render in
      // onComplete replaces the entire board DOM, clearing every transform in
      // one pass.
      runStep()
    }

    // Arm the fallback timer SYNCHRONOUSLY here, BEFORE the requestAnimationFrame
    // below — not inside the rAF callback. This is the resume-lockup fix: when
    // the tab is backgrounded, iOS Safari can stop servicing rAF entirely, so a
    // callback scheduled via requestAnimationFrame may never run. If the timer
    // were armed inside that rAF, a background gap right after a flick would
    // leave NO timer and NO transitionend, and isAnimating would never clear —
    // a permanent input lock. setTimeout, by contrast, still fires (throttled)
    // while backgrounded and on resume, so arming it here means the latch
    // ALWAYS breaks: this timer self-heals the animation on resume on its own,
    // independent of the resume reconciler.
    //
    // Sizing: 1.5x duration + 50ms. Generous on purpose — background throttling
    // can delay transitionend by hundreds of ms — and now this window also has
    // to span the rAF gap (the transform write happens a frame later), so the
    // grace covers both the throttled transition and the deferred start.
    timerId = window.setTimeout((): void => {
      onEnd()
    }, Math.ceil(durationMs * 1.5) + 50)
    // NOT { once: true }: onEnd filters foreign transitionend events (wrong
    // target/property) and must keep listening after ignoring one; the accept
    // path removes the listener explicitly.
    el.addEventListener('transitionend', onEnd)

    // Defer ONLY the transform write to the next animation frame so the browser
    // registers the transition before the property changes. Without this rAF,
    // some browsers jump straight to the destination without animating. The
    // fallback timer above intentionally lives OUTSIDE this rAF so a dropped
    // frame (backgrounded tab) can't leave the latch un-armed.
    requestAnimationFrame(() => {
      el.style.transform = `translate3d(${String(dx)}px, ${String(dy)}px, 0)`
    })
  }

  runStep()
}

// Compute the pixel translate for a (direction, distance) pair. Pure; exported
// for test isolation.
//
// For non-exit steps, `distance` is the manhattan-cell distance between source
// and destination. For exit steps, the same formula gives a translate that
// puts the tile one cell PAST the board edge in the cascade direction — the
// board's `overflow: hidden` clips the tile during its exit slide.
export function computeTranslate(
  direction: Direction,
  distance: number,
  cellSizePx: number,
): readonly [number, number] {
  const d = distance * cellSizePx
  switch (direction) {
    case 'UP':
      return [0, -d] as const
    case 'DOWN':
      return [0, d] as const
    case 'LEFT':
      return [-d, 0] as const
    case 'RIGHT':
      return [d, 0] as const
  }
}

// Centralised reduced-motion check. Currently a thin wrapper over the media
// query, but kept as a function so the upcoming settings-driven "reduce
// motion" toggle (out of scope for Phase 7) can hook here without touching
// every call site. Defensive against window.matchMedia being absent in
// stripped-down test environments.
export function shouldReduceMotion(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// EXIT_CELL is re-exported here so consumers of animator.ts can recognise
// terminal steps without pulling in state.ts. Convenience only.
export { EXIT_CELL }
